import { useState } from 'react';
import { t } from '@app/i18n';
import { Link } from 'react-router-dom';
import {
  Badge,
  Card,
  Chip,
  DataTable,
  DrawerPresence,
  IconButton,
  PAGE_SIZE,
  PageHeader,
  type DataTableColumn,
} from '@zarya/ui';
import { dataHour } from '@app/format';
import { useDrawerParam } from '@app/navigation';
import { useAudit } from './Audit.api';
import {
  FILTERS_INICIAIS,
  entityLink,
  resultView,
  hasFilterApplied,
  type AuditEntry,
  type AuditFilterValues,
} from './AuditModel';
import { AuditFilters } from './AuditFilters';
import { AuditDetailDrawer } from './AuditDetailDrawer';
import s from './Audit.module.css';

/**
 * T23 — Auditoria (Story 13).
 *
 * Leitura para todos os papéis e nenhuma ação de escrita (13.8): o registro é o
 * que a auditoria tem a oferecer, e ele não se edita. Toda mutação do produto
 * cai aqui — inclusive as que o executor recusou, que são justamente as que
 * provam a regra "falhou → não persiste".
 */
export function AuditPage() {
  const [filters, setFiltros] = useState<AuditFilterValues>(FILTERS_INICIAIS);
  const drawer = useDrawerParam();
  const lista = useAudit(filters);

  const itens = lista.data?.items ?? [];
  const filtrado = hasFilterApplied(filters);

  function swapFilters(mudanca: Partial<AuditFilterValues>) {
    setFiltros((current) => ({ ...current, page: 1, ...mudanca }));
  }

  const columns: DataTableColumn<AuditEntry>[] = [
    {
      key: 'when',
      header: t('audit.list.column.when'),
      width: '190px',
      cell: (entry) => dataHour(entry.when),
    },
    {
      key: 'user',
      header: t('audit.list.column.user'),
      cell: (entry) =>
        entry.user === 'plataforma' ? (
          <Chip icon="robot">{t('audit.platform')}</Chip>
        ) : (
          <span className={s.mono}>{entry.user}</span>
        ),
    },
    {
      key: 'action',
      header: t('audit.list.column.action'),
      mono: true,
      cell: (entry) => entry.action,
    },
    {
      key: 'entidade',
      header: t('audit.list.column.entity'),
      cell: (entry) => {
        const rota = entityLink(entry.entity_kind, entry.entity_id);
        const rotulo = entry.entity_name ?? entry.entity_id ?? '—';
        return (
          <span className={s.entidade}>
            <span className={s.tipo}>{entry.entity_kind}</span>
            {rota ? (
              // Este link LEVA para a entidade; a linha ABRE a gaveta. São dois destinos,
              // e sem parar a propagação o mesmo clique faz os dois: a pessoa muda de tela
              // com uma gaveta aberta por cima do registro errado.
              <Link className={s.link} to={rota} onClick={(event) => event.stopPropagation()}>
                {rotulo}
              </Link>
            ) : (
              rotulo
            )}
          </span>
        );
      },
    },
    {
      key: 'result',
      header: t('audit.list.column.result'),
      width: '190px',
      tip: t('audit.list.column.result.tip'),
      cell: (entry) => {
        const view = resultView(entry);
        return <Badge tone={view.tom}>{view.text}</Badge>;
      },
    },
    {
      key: 'actions',
      header: t('audit.list.column.actions'),
      srOnlyHeader: true,
      width: '64px',
      cell: (entry) => (
        <IconButton
          icon="file-search"
          size="sm"
          label={t('audit.list.action.details', { action: entry.action })}
          onClick={(event) => {
            // A linha inteira também abre a gaveta. Sem parar aqui, o mesmo clique
            // chamaria `abrir` duas vezes — e cada chamada empilha uma entrada no
            // histórico, então o «voltar» passaria a precisar de dois toques.
            event.stopPropagation();
            drawer.abrir(entry.id);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('audit.list.title')}
        tip={t('audit.list.purpose')}
        icon="clock"
        badges={
          typeof lista.data?.total === 'number' ? <Chip>{t('audit.list.total', { n: lista.data.total })}</Chip> : null
        }
      />

      <Card bare aria-label={t('audit.list.card.label')}>
        <DataTable
          label={t('audit.list.table.label')}
          columns={columns}
          rows={itens}
          rowKey={(entry) => entry.id}
          onRowClick={(entry) => drawer.abrir(entry.id)}
          loading={lista.isLoading}
          error={lista.error}
          onRetry={() => void lista.refetch()}
          filters={<AuditFilters filtros={filters} onChange={swapFilters} />}
          filtersApplied={filtrado}
          onClearFilters={() => setFiltros(FILTERS_INICIAIS)}
          page={filters.page}
          pageSize={PAGE_SIZE}
          total={lista.data?.total}
          onPageChange={(page) => setFiltros((current) => ({ ...current, page }))}
          emptyIcon="clock"
          emptyTitle={t(
            filtrado ? 'audit.list.empty.filtered.title' : 'audit.list.empty.none.title',
          )}
          emptyMessage={
            filtrado
              ? t('audit.list.empty.filtered.message')
              : t('audit.list.empty.none.message')
          }
        />
      </Card>

      <DrawerPresence>
        {drawer.id ? <AuditDetailDrawer id={drawer.id} onClose={drawer.fechar} /> : null}
      </DrawerPresence>
    </>
  );
}
